/**
 * Release Status Mockup — shows build + app store submission status for an app.
 * Uses static mock data for demo/presentation purposes.
 */
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import {
  GitPullRequest, CheckCircle2, Loader2, Package, ExternalLink,
  Smartphone, ArrowRight, Clock, AlertCircle, Rocket,
} from 'lucide-react'

interface Props {
  appSlug: string
  appName: string
  hasPR: boolean
}

// Mock data for demo
const mockData: Record<string, {
  pr: { number: number; status: string; mergedAt: string | null; url: string }
  builds: Array<{ platform: string; status: string; version: string; buildNumber: number }>
  submissions: Array<{ store: string; track: string; status: string; submittedAt: string | null }>
}> = {
  'block-blitz-game': {
    pr: { number: 3, status: 'merged', mergedAt: '2026-03-24T14:30:00Z', url: 'https://bitbucket.org/glaium/block-blitz-game/pull-requests/3' },
    builds: [
      { platform: 'Android', status: 'completed', version: '2.4.1', buildNumber: 148 },
      { platform: 'iOS', status: 'completed', version: '2.4.1', buildNumber: 148 },
    ],
    submissions: [
      { store: 'Google Play', track: 'Internal Testing', status: 'available', submittedAt: '2026-03-24T15:00:00Z' },
      { store: 'App Store', track: 'TestFlight Internal', status: 'processing', submittedAt: '2026-03-24T15:05:00Z' },
    ],
  },
  'contraction-tracker-app': {
    pr: { number: 5, status: 'open', mergedAt: null, url: 'https://bitbucket.org/glaium/contraction-tracker-app/pull-requests/5' },
    builds: [],
    submissions: [],
  },
}

const defaultMock = {
  pr: { number: 0, status: 'none', mergedAt: null, url: '' },
  builds: [],
  submissions: [],
}

const buildStatusConfig: Record<string, { color: string; bg: string; icon: typeof CheckCircle2 }> = {
  completed: { color: 'text-emerald-600', bg: 'bg-emerald-50', icon: CheckCircle2 },
  building: { color: 'text-blue-600', bg: 'bg-blue-50', icon: Loader2 },
  pending: { color: 'text-slate-400', bg: 'bg-slate-50', icon: Clock },
  failed: { color: 'text-red-600', bg: 'bg-red-50', icon: AlertCircle },
}

const submissionStatusConfig: Record<string, { label: string; color: string; bg: string }> = {
  available: { label: 'Available', color: 'text-emerald-700', bg: 'bg-emerald-50' },
  processing: { label: 'Processing', color: 'text-blue-700', bg: 'bg-blue-50' },
  in_review: { label: 'In Review', color: 'text-amber-700', bg: 'bg-amber-50' },
  approved: { label: 'Approved', color: 'text-emerald-700', bg: 'bg-emerald-50' },
  rejected: { label: 'Rejected', color: 'text-red-700', bg: 'bg-red-50' },
  pending: { label: 'Pending', color: 'text-slate-500', bg: 'bg-slate-50' },
}

export function GuardianReleaseStatus({ appSlug, appName, hasPR }: Props) {
  const data = mockData[appSlug] || defaultMock

  if (!hasPR && data.pr.status === 'none') {
    return null
  }

  return (
    <div className="border-t mt-4 pt-4 space-y-4">
      <div className="flex items-center gap-2">
        <Rocket className="h-4 w-4 text-slate-400" />
        <h4 className="text-sm font-semibold text-slate-900">Release Pipeline</h4>
      </div>

      {/* Pipeline Steps */}
      <div className="flex items-center gap-2 text-[11px]">
        {/* PR Status */}
        <div className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg border ${
          data.pr.status === 'merged' ? 'bg-emerald-50 border-emerald-200' :
          data.pr.status === 'open' ? 'bg-blue-50 border-blue-200' : 'bg-slate-50 border-slate-200'
        }`}>
          <GitPullRequest className={`h-3.5 w-3.5 ${data.pr.status === 'merged' ? 'text-emerald-600' : data.pr.status === 'open' ? 'text-blue-600' : 'text-slate-400'}`} />
          <span className="font-medium">PR #{data.pr.number}</span>
          <Badge variant="secondary" className={`text-[9px] px-1 border-0 ${
            data.pr.status === 'merged' ? 'bg-emerald-100 text-emerald-700' :
            data.pr.status === 'open' ? 'bg-blue-100 text-blue-700' : 'bg-slate-100 text-slate-500'
          }`}>{data.pr.status}</Badge>
        </div>

        {data.pr.status === 'merged' && (
          <>
            <ArrowRight className="h-3.5 w-3.5 text-slate-300" />

            {/* Build Status */}
            <div className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg border ${
              data.builds.length > 0 && data.builds.every(b => b.status === 'completed') ? 'bg-emerald-50 border-emerald-200' :
              data.builds.some(b => b.status === 'building') ? 'bg-blue-50 border-blue-200' : 'bg-slate-50 border-slate-200'
            }`}>
              <Package className="h-3.5 w-3.5 text-slate-500" />
              <span className="font-medium">Build</span>
              {data.builds.length > 0 ? (
                <Badge variant="secondary" className="text-[9px] px-1 border-0 bg-emerald-100 text-emerald-700">
                  {data.builds.filter(b => b.status === 'completed').length}/{data.builds.length} ready
                </Badge>
              ) : (
                <Badge variant="secondary" className="text-[9px] px-1 border-0 bg-slate-100 text-slate-500">waiting</Badge>
              )}
            </div>

            <ArrowRight className="h-3.5 w-3.5 text-slate-300" />

            {/* Submit Status */}
            <div className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg border ${
              data.submissions.some(s => s.status === 'available') ? 'bg-emerald-50 border-emerald-200' :
              data.submissions.some(s => s.status === 'processing') ? 'bg-blue-50 border-blue-200' : 'bg-slate-50 border-slate-200'
            }`}>
              <Smartphone className="h-3.5 w-3.5 text-slate-500" />
              <span className="font-medium">Store</span>
              {data.submissions.length > 0 ? (
                <Badge variant="secondary" className="text-[9px] px-1 border-0 bg-blue-100 text-blue-700">
                  {data.submissions.filter(s => s.status === 'available').length}/{data.submissions.length} live
                </Badge>
              ) : (
                <Badge variant="secondary" className="text-[9px] px-1 border-0 bg-slate-100 text-slate-500">pending</Badge>
              )}
            </div>
          </>
        )}

        {data.pr.url && (
          <a href={data.pr.url} target="_blank" rel="noopener noreferrer"
            className="ml-auto text-blue-600 hover:text-blue-800 flex items-center gap-1">
            View PR <ExternalLink className="h-3 w-3" />
          </a>
        )}
      </div>

      {/* Build Details */}
      {data.builds.length > 0 && (
        <div className="grid grid-cols-2 gap-3">
          {data.builds.map(build => {
            const cfg = buildStatusConfig[build.status] || buildStatusConfig.pending
            const StatusIcon = cfg.icon
            return (
              <div key={build.platform} className={`flex items-center justify-between px-3 py-2.5 rounded-lg border ${cfg.bg}`}>
                <div className="flex items-center gap-2">
                  <StatusIcon className={`h-4 w-4 ${cfg.color} ${build.status === 'building' ? 'animate-spin' : ''}`} />
                  <div>
                    <p className="text-xs font-medium text-slate-900">{build.platform}</p>
                    <p className="text-[10px] text-slate-500">v{build.version} ({build.buildNumber})</p>
                  </div>
                </div>
                <Badge variant="secondary" className={`text-[10px] border-0 ${cfg.bg} ${cfg.color}`}>
                  {build.status === 'completed' ? 'Ready' : build.status}
                </Badge>
              </div>
            )
          })}
        </div>
      )}

      {/* Store Submission Details */}
      {data.submissions.length > 0 && (
        <div className="space-y-2">
          <p className="text-xs font-medium text-slate-700">Store Submissions</p>
          {data.submissions.map(sub => {
            const cfg = submissionStatusConfig[sub.status] || submissionStatusConfig.pending
            return (
              <div key={sub.store} className="flex items-center justify-between px-3 py-2.5 rounded-lg border bg-white">
                <div className="flex items-center gap-3">
                  <div className={`h-8 w-8 rounded-lg ${sub.store === 'Google Play' ? 'bg-emerald-50' : 'bg-slate-100'} flex items-center justify-center`}>
                    {sub.store === 'Google Play' ? (
                      <Smartphone className="h-4 w-4 text-emerald-600" />
                    ) : (
                      <svg className="h-4 w-4 text-slate-700" viewBox="0 0 24 24" fill="currentColor"><path d="M18.71 19.5c-.83 1.24-1.71 2.45-3.05 2.47-1.34.03-1.77-.79-3.29-.79-1.53 0-2 .77-3.27.82-1.31.05-2.3-1.32-3.14-2.53C4.25 17 2.94 12.45 4.7 9.39c.87-1.52 2.43-2.48 4.12-2.51 1.28-.02 2.5.87 3.29.87.78 0 2.26-1.07 3.8-.91.65.03 2.47.26 3.64 1.98-.09.06-2.17 1.28-2.15 3.81.03 3.02 2.65 4.03 2.68 4.04-.03.07-.42 1.44-1.38 2.83M13 3.5c.73-.83 1.94-1.46 2.94-1.5.13 1.17-.34 2.35-1.04 3.19-.69.85-1.83 1.51-2.95 1.42-.15-1.15.41-2.35 1.05-3.11z"/></svg>
                    )}
                  </div>
                  <div>
                    <p className="text-xs font-medium text-slate-900">{sub.store}</p>
                    <p className="text-[10px] text-slate-500">{sub.track}</p>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <Badge variant="secondary" className={`text-[10px] border-0 ${cfg.bg} ${cfg.color}`}>
                    {cfg.label}
                  </Badge>
                  {sub.status === 'available' && (
                    <Button variant="outline" size="sm" className="h-6 text-[10px] px-2 gap-1">
                      <ArrowRight className="h-3 w-3" />
                      Promote to Production
                    </Button>
                  )}
                  {sub.status === 'processing' && (
                    <span className="text-[10px] text-slate-400 flex items-center gap-1">
                      <Loader2 className="h-3 w-3 animate-spin" /> ~15 min
                    </span>
                  )}
                </div>
              </div>
            )
          })}

          <div className="flex gap-2 pt-1">
            <Button size="sm" className="h-7 text-xs gap-1.5">
              <Smartphone className="h-3.5 w-3.5" />
              Submit to Both Stores
            </Button>
            <Button variant="outline" size="sm" className="h-7 text-xs gap-1.5">
              Configure Release
            </Button>
          </div>
        </div>
      )}

      {/* PR is open but not merged — show waiting state */}
      {data.pr.status === 'open' && (
        <div className="flex items-center gap-2 px-3 py-3 rounded-lg bg-slate-50 border border-slate-200 text-xs text-slate-500">
          <Clock className="h-4 w-4" />
          <span>Waiting for PR #{data.pr.number} to be merged before build and submission can proceed.</span>
        </div>
      )}
    </div>
  )
}
